// The phone's cache of synced data (REQ-OFF-03): reference data, open invoices and items, animals,
// counts and places of the user's properties, refreshed at every sync.
import { db } from "./db";
import { dataClient } from "../lib/supabase";
import { markSavedCopy } from "./savedCopy";

type CacheSpec = { table: string; select: string; build?: (q: any) => any; idField?: string };

const CACHED: CacheSpec[] = [
  { table: "species", select: "*" },
  { table: "company", select: "*", build: (q) => q.is("voided_at", null) },
  { table: "property", select: "*", build: (q) => q.is("voided_at", null) },
  { table: "v_place_geo", select: "*", idField: "place_id" },
  { table: "person", select: "id, full_name, team, is_active", build: (q) => q.eq("is_active", true) },
  { table: "counterparty", select: "id, name", build: (q) => q.is("voided_at", null) },
  { table: "v_invoice_summary", select: "*", idField: "invoice_id" },
  { table: "v_item_state", select: "*", idField: "item_id", build: (q) => q.eq("still_to_come", true) },
  { table: "invoice_line", select: "*, seller:seller_counterparty_id(name, phone, email), species:species_id(name, code)" },
  { table: "v_animal_list", select: "*", idField: "animal_id" },
  { table: "v_population_current", select: "*", idField: "place_id" },
  { table: "setting", select: "key, value", idField: "key" },
  { table: "calf_at_foot", select: "*, mother:mother_animal_id(register_id), species:species_id(name, code)", build: (q) => q.eq("status", "at_foot") }
];

// A table whose rows are known by more than one column. v_population_current has one row per place, species and sex.
const KEY_FIELDS: Record<string, string[]> = { v_population_current: ["place_id", "species_id", "sex"] };

/**
 * The id of a cached row: the same for the same row whichever writer caches it (the refresh or a screen's read), so a
 * newer read replaces the row instead of sitting beside it or collapsing several rows into one.
 */
function cacheId(table: string, row: Record<string, unknown>, idField = "id", index = 0): string {
  const fields = KEY_FIELDS[table];
  return fields ? fields.map((f) => String(row[f])).join(":") : String(row[idField] ?? index);
}

export async function refreshCache(): Promise<boolean> {
  // The refresh's data is only as new as its start: a change saved while it runs may be missing from it.
  const started = new Date().toISOString();
  let complete = true;
  for (const spec of CACHED) {
    const askedAt = Date.now();
    let q = dataClient.from(spec.table).select(spec.select).limit(5000);
    if (spec.build) q = spec.build(q);
    const { data, error } = await q;
    if (error || !data) {
      console.warn(`The phone could not refresh ${spec.table}: ${error?.message ?? "no data"}`);
      complete = false;
      continue;
    }
    const idField = spec.idField ?? "id";
    await db.transaction("rw", db.cache, db.meta, async () => {
      // A screen may have read this table again while the download was under way (after a KML import, say): its rows
      // are newer than this download and are kept. Every older row is replaced, and one the server no longer has goes.
      // A read time later than now was written while the phone's clock ran ahead; it is not newer, or the row would
      // outlive every refresh until real time caught up (a renamed place keeping its old name, a deleted one staying).
      const existing = await db.cache.where("table").equals(spec.table).toArray();
      const now = Date.now();
      const newer = new Set(existing.filter((r) => (r.read_at ?? 0) >= askedAt && (r.read_at ?? 0) <= now).map((r) => r.key));
      await db.cache.bulkDelete(existing.filter((r) => !newer.has(r.key)).map((r) => r.key));
      await db.meta.put({ key: `cache_at:${spec.table}`, value: new Date(askedAt).toISOString() });
      await db.cache.bulkPut((data as unknown as Record<string, unknown>[]).flatMap((row, i) => {
        const id = cacheId(spec.table, row, idField, i);
        const key = `${spec.table}:${id}`;
        return newer.has(key) ? [] : [{ key, table: spec.table, id, data: row, read_at: askedAt }];
      }));
    });
  }
  // When the last refresh started, complete or not. Tests wait for one that started after their change before going
  // offline (goOffline); a refresh that started earlier and finished later may not hold that change.
  await db.meta.put({ key: "cache_refreshed_at", value: started });
  if (complete) await db.meta.put({ key: "cache_complete_at", value: started });
  return complete;
}

/**
 * The saved rows of a table that `keep` asks for, marked as the phone's saved copy with when it was read (ED-RK-005): as old
 * as the oldest of them, from the same rows (a row with no read time of its own counts as read at the table's last refresh,
 * `cache_at:<table>`; with neither, "unknown").
 */
export async function readCache<T = Record<string, any>>(table: string, keep: (row: T) => boolean = () => true): Promise<T[]> {
  const held = (await db.cache.where("table").equals(table).toArray()).filter((r) => keep(r.data as T));
  if (!held.length) return [];
  const refreshed = held.some((r) => r.read_at === undefined) ? Date.parse(String((await db.meta.get(`cache_at:${table}`))?.value)) : NaN;
  const times = held.map((r) => r.read_at ?? refreshed);
  return markSavedCopy(held.map((r) => r.data as T), times.some(Number.isNaN) ? "unknown" : Math.min(...times));
}

/**
 * Writes rows fetched online into the cache, so the same list works offline later. `readAt` is when the read was asked
 * for; a refresh that started before it keeps these rows.
 */
export async function writeCache(table: string, rows: Record<string, unknown>[], idField = "id", readAt = Date.now()): Promise<void> {
  await db.cache.bulkPut(rows.map((row, i) => {
    const id = cacheId(table, row, idField, i);
    return { key: `${table}:${id}`, table, id, data: row, read_at: readAt };
  }));
}
